import { Box, Button, Stack, Typography } from "@mui/material";
import {
	poHeaderColumnsObj,
	poHeaderEditableGridColumnList,
	poHeaderChannelDeliveryColumnList,
	syncPoHeaderDraft,
	syncPoVendorDefaults,
} from "../data";
import PoGeneralHeaderGrid from "../PoGeneralHeaderGrid";
import PoLineItemsSection from "../PoLineItemsSection/PoLineItemsSection";
import DisplayPoHeaderDetails from "../DisplayPoHeaderDetails/DisplayPoHeaderDetails";
import DisplayPoItemDetails from "../DisplayPoItemDetails/DisplayPoItemDetails";
import PoCostSummary from "../PoCostSummary";
import { PoPanel, poColors, poPrimaryBtnSx } from "../poUi";

/**
 * @param {"create"|"selected"} variant
 * create → editable header inputs + sticky Create
 * selected → view + section Edit/Save modals (persist via onSectionSave)
 */
const PurchaseOrderForm = ({
	value,
	onChange,
	readOnly = false,
	onSave,
	onSectionSave,
	saving = false,
	variant = "create",
	masterLabels = {},
}) => {
	const form = value || {};
	const isSelectedView = variant === "selected";

	const setForm = (updater) => {
		onChange((prev) => {
			const base = prev || {};
			const next = typeof updater === "function" ? updater(base) : updater;
			return syncPoHeaderDraft(base, next);
		});
	};

	const handleHeaderSave = async (headerDraft) => {
		const next = syncPoVendorDefaults({
			...form,
			...headerDraft,
			items: form.items || [],
			set_items: form.set_items || [],
			document_charges: form.document_charges || [],
			line_charges: form.line_charges || [],
			receive_schedules: form.receive_schedules || [],
		});
		onChange(next);
		if (typeof onSectionSave === "function") {
			await onSectionSave(next);
		}
	};

	const handleItemsSave = async (items) => {
		const next = { ...form, items };
		onChange(next);
		if (typeof onSectionSave === "function") {
			await onSectionSave(next);
		}
	};

	const handleItemsChange = (items) => {
		onChange({ ...form, items });
	};

	return (
		<Stack spacing={2}>
			{isSelectedView ? (
				<PoPanel contentSx={{ pt: 1.5, pb: 1.5 }}>
					<DisplayPoHeaderDetails
						data={form}
						onSave={handleHeaderSave}
						disableEdit={readOnly}
						saving={saving}
						masterLabels={masterLabels}
					/>
				</PoPanel>
			) : (
				<PoPanel title="General" contentSx={{ px: 1.75, py: 1.25 }}>
					<PoGeneralHeaderGrid
						columns={poHeaderEditableGridColumnList}
						rowColumns={poHeaderChannelDeliveryColumnList}
						footerColumns={[poHeaderColumnsObj.remarks]}
						data={form}
						setData={setForm}
						readOnly={readOnly}
					/>
				</PoPanel>
			)}

			{(form.entry_mode || "I") === "I" &&
				(isSelectedView ? (
					<>
						<DisplayPoItemDetails items={form.items || []} onSave={handleItemsSave} disableEdit={readOnly} saving={saving} />
						<PoCostSummary form={form} />
					</>
				) : (
					<PoLineItemsSection items={form.items || []} onChange={handleItemsChange} readOnly={readOnly} />
				))}

			{!isSelectedView && !readOnly && (
				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						gap: 1,
						justifyContent: "flex-start",
						px: 2,
						py: 1.5,
						bgcolor: poColors.white,
						border: `1px solid ${poColors.line}`,
						borderRadius: "14px",
						position: "sticky",
						bottom: 8,
						zIndex: 2,
						boxShadow: "0 -4px 20px rgba(0,0,0,0.04)",
					}}>
					<Button variant="contained" onClick={onSave} disabled={saving} sx={poPrimaryBtnSx}>
						{saving ? "Creating…" : "Create PO"}
					</Button>
				</Box>
			)}

			{readOnly && (
				<Box
					sx={{
						px: 2,
						py: 1.25,
						borderRadius: "12px",
						border: `1px solid ${poColors.warnBg}`,
						bgcolor: poColors.warnBg,
					}}>
					<Typography sx={{ fontSize: 13, fontWeight: 600, color: poColors.warn }}>
						This PO is published and locked for editing.
					</Typography>
				</Box>
			)}
		</Stack>
	);
};

export default PurchaseOrderForm;
